import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { RedirectionStateParserService } from './redirection-state-parser.service';

// Kept in sessionStorage, the same store the OIDC library uses for its tokens. localStorage
// outlives the tab and is shared between tabs, which let an abandoned login resurface much
// later — and against whichever application id happened to be written last.
const LOGIN_INTENT_KEY = 'landing-page-login-intent';

export interface ILoginIntent {
  applicationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  constructor(
    private readonly oauthService: OAuthService,
    private readonly redirectionStateService: RedirectionStateParserService
  ) { }

  async logOut(applicationId: string) {
    await this.oauthService.loadDiscoveryDocument();
    this.oauthService.logOut(false, this.redirectionStateService.generateStateString(applicationId, true));
  }

  async logIn(applicationId: string) {
    sessionStorage.setItem(LOGIN_INTENT_KEY, JSON.stringify({ applicationId } as ILoginIntent));
    // Loaded here rather than eagerly at bootstrap so there is no race between the app
    // component and the redirect guard over which one has the discovery document.
    await this.oauthService.loadDiscoveryDocument();
    this.oauthService.initCodeFlow(applicationId);
  }

  async handleUnauthorizedInAltinn(applicationId: string) {
    await this.oauthService.loadDiscoveryDocument();
    this.oauthService.logOut(false, this.redirectionStateService.generateStateString(applicationId, false));
  }

  // Read-and-clear: an intent must never be consumable twice, or a later visit could act on
  // a login that already happened.
  takeLoginIntent(): ILoginIntent | null {
    const stored = sessionStorage.getItem(LOGIN_INTENT_KEY);
    sessionStorage.removeItem(LOGIN_INTENT_KEY);

    if (stored === null) {
      return null;
    }

    try {
      return JSON.parse(stored) as ILoginIntent;
    } catch {
      return null;
    }
  }
}
